import { useUserOrgId } from '@/auth/authentication';
import { If } from '@/components/utils/conditionalComponents';
import { OrganizationBasic } from '@/fetching/sharedSchemas/basicSchemas';
import { AxleSuspense } from '@/fetching/suspense';
import { useInputState } from '@/utils/inputState';
import { sortAndUniqBy } from '@/utils/iteration';
import { ReturnComponent } from '@/utils/reactTypes';
import { Box } from '@mui/material';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { AxleAutocompleteField } from './AxleAutocompleteField';

//////////////////////////////////////////////////////////////////////////////
// region Non-suspense version, that stores the state in an InputState.
//////////////////////////////////////////////////////////////////////////////

type SelectAllOptionProps =
    | {
          showOptionToSelectAll: true;
          defaultToAll: boolean;
      }
    | {
          showOptionToSelectAll: false;
          defaultToAll?: false;
      };

// Sort and deduplicate the given organization objects.
//  You don't have to use this; if you don't, the organizations will be sorted
//  and deduplicated for you.
const sortAndUniqOrgs = (orgs: OrganizationBasic[]): OrganizationBasic[] =>
    sortAndUniqBy(orgs, {
        uniqBy: 'uuid',
        sortBy: 'name',
    });

// Create InputState to store the selected organization.
export const useOrgDropdownState = ({
    // Whether to show an “All” option at the top of the list.
    showOptionToSelectAll,
    // If true, then the “All” option will be the initially-selected value.
    //  Otherwise, the user’s organization will be the initially-selected value.
    //  This can only be true if `showOptionToSelectAll` is true.
    defaultToAll = false,
    // The organization to start on, for callers whose set of options does not
    //  necessarily contain the user’s organization. Defaults to the user’s
    //  organization.
    defaultOrgId,
}: SelectAllOptionProps & { defaultOrgId?: string | undefined }) => {
    const userOrgId = useUserOrgId();
    // The empty string represents the “All” option.
    const defaultValue = defaultToAll ? '' : defaultOrgId ?? userOrgId;
    // We only allow blank values if the “All” option is to be allowed.
    const isInputStateRequired = !showOptionToSelectAll;
    return useInputState<string>(defaultValue, { required: isInputStateRequired });
};

// Dropdown to let the user select an organization.
export const OrgDropdown = ({
    inputState,
    options: potentiallyUnsortedOptions,
}: {
    inputState: ReturnType<typeof useOrgDropdownState>;
    options: OrganizationBasic[];
}): ReturnComponent => {
    const userOrgId = useUserOrgId();
    const sortedOptions = sortAndUniqOrgs(potentiallyUnsortedOptions);

    const hasMoreThanOneOption = sortedOptions.length > 1;
    // Sometimes there are 0 options, such as if the QHP table has no QHPs.
    // Sometimes there is only 1 option, such as if the user only has access to one organization.

    // In case there is only one option, and it's the user's org, select it.
    //  This is important, so we do not get stuck with the default value of '',
    //  which means show all orgs, which ends up displaying the org column in tables.
    useEffect(() => {
        if (
            sortedOptions.length === 1 &&
            sortedOptions[0].uuid === userOrgId &&
            inputState.value !== sortedOptions[0].uuid
        ) {
            inputState.setInputState(sortedOptions[0].uuid, { isTouched: false });
        }
    }, [sortedOptions, inputState, userOrgId]);

    const showOptionToSelectAll = !inputState.required;
    const selectAllOption: OrganizationBasic = { uuid: '', name: 'All' };
    const finalOptions = showOptionToSelectAll
        ? [selectAllOption, ...sortedOptions]
        : sortedOptions;

    return (
        <If condition={hasMoreThanOneOption}>
            <Box width={'15em'}>
                <AxleAutocompleteField
                    label={''}
                    hideHelperText={true}
                    inputState={inputState}
                    options={finalOptions}
                    optionToValue={(org) => org.uuid}
                    optionToLabel={(org) => org.name}
                    // Sorting the option messes with making sure the “All” option is always the first option.
                    sortOptions={false}
                />
            </Box>
        </If>
    );
};

// Helper to filter your set of rows with the selected org state.
//  It takes into account the “All” option!
export const filterRowsBySelectedOrg = <Row,>({
    rows,
    rowToOrg,
    orgDropdownStateValue,
}: {
    rows: Row[];
    rowToOrg: (row: Row) => OrganizationBasic;
    orgDropdownStateValue: string;
}): [Row[], boolean] => {
    const selectedRows =
        orgDropdownStateValue === ''
            ? rows
            : rows.filter((row) => rowToOrg(row).uuid === orgDropdownStateValue);
    const hideOrgColumn = orgDropdownStateValue !== ''; // Hide the org column if we're filtering by a specific org.
    return [selectedRows, hideOrgColumn];
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Suspense-friendly version, that stores the state in an atom.
//////////////////////////////////////////////////////////////////////////////

type AtomSyncedOrgDropdownProps<Data> = SelectAllOptionProps & {
    fetchData: () => Data;
    dataToOrgs: (data: Data) => OrganizationBasic[];
    orgDropdownAtom: ReturnType<typeof makeOrgDropdownAtom>;
};

const AtomSyncedOrgDropdown = <Data,>({
    showOptionToSelectAll,
    defaultToAll,
    fetchData,
    dataToOrgs,
    orgDropdownAtom,
}: AtomSyncedOrgDropdownProps<Data>): ReturnComponent => {
    const userOrgId = useUserOrgId();
    const data = fetchData();
    const orgs = sortAndUniqOrgs(dataToOrgs(data));

    const defaultOrgId = defaultToAll ? '' : userOrgId;
    const setAtomValue = useSetAtom(orgDropdownAtom);
    const inputState = useInputState(defaultOrgId, {
        required: !showOptionToSelectAll,
        onChangeCallbacks: [setAtomValue],
    });

    return <OrgDropdown inputState={inputState} options={orgs} />;
};

// Create an atom to store the selected organization.
//  The default value of '' here doesn't matter; it is overwritten when the org dropdown first loads anyway.
export const makeOrgDropdownAtom = () => atom<string>('');

// Suspense-friendly dropdown to select an organization from a React Query function you pass in.
export const SuspenseFriendlyOrgDropdown = <Data,>(
    props: AtomSyncedOrgDropdownProps<Data>,
): ReturnComponent => {
    return (
        // Because no skeletonWidth is provided, the skeleton often ends up having zero width
        //  when inside an <AxlePageHeader/> component, and so it is not visible, which is just fine
        //  for us. We could set an explicit skeletonWidth here, but I think it actually looks
        //  better for the skeleton to be invisible until the component is ready.
        <AxleSuspense skeletonHeight={'1em'}>
            <AtomSyncedOrgDropdown {...props} />
        </AxleSuspense>
    );
};

// Helper to filter your set of rows with the selected org state.
export const useFilterRowsBySelectedOrg = <Row,>({
    rows,
    rowToOrg,
    orgDropdownAtom,
}: {
    rows: Row[];
    rowToOrg: (row: Row) => OrganizationBasic;
    orgDropdownAtom: ReturnType<typeof makeOrgDropdownAtom>;
}): [Row[], boolean] => {
    const selectedOrgId = useAtomValue(orgDropdownAtom);
    return filterRowsBySelectedOrg({ rows, rowToOrg, orgDropdownStateValue: selectedOrgId });
};

// endregion
